import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useAuth } from "@/components/AuthProvider";

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { getPath } = useLanguagePath();
  const { user, signOut } = useAuth();

  // Toggle mobile menu
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMenuOpen && !target.closest(".mobile-menu") && !target.closest(".menu-toggle")) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Handle clicking on navigation item
  const handleNavItemClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const currentPath = location.pathname;
    const homePath = getPath('/');
    const isHome = currentPath === homePath;
    
    if (isHome) {
      // If on home page, scroll to section
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        setIsMenuOpen(false);
      }
    } else {
      // If not on home page, navigate to home with hash
      navigate(getPath(`/#${id}`));
      setIsMenuOpen(false);
      
      // Add a small delay to allow the page to load before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  return (
    <nav
      className={`fixed top-0 z-40 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-white dark:bg-gray-900/95 backdrop-blur-md shadow-sm py-2"
          : "bg-white dark:bg-gray-900 py-3"
      }`}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to={getPath('/')} className="flex items-center">
            <img
              src="/lovable-uploads/a0ac6ad1-1a90-4726-9a01-65aaa671437c.png"
              alt="TrainerFOLIO Logo"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <a
              href="#features"
              className="text-slate-600 dark:text-gray-300 hover:text-flame dark:hover:text-flame font-medium"
              onClick={(e) => handleNavItemClick(e, "features")}
            >
              {t('landing.footer.usefulLinks.features')}
            </a>
            <a
              href="#pricing"
              className="text-slate-600 dark:text-gray-300 hover:text-flame dark:hover:text-flame font-medium"
              onClick={(e) => handleNavItemClick(e, "pricing")}
            >
              {t('landing.footer.usefulLinks.pricing')}
            </a>
            <a
              href="#testimonials"
              className="text-slate-600 dark:text-gray-300 hover:text-flame dark:hover:text-flame font-medium"
              onClick={(e) => handleNavItemClick(e, "testimonials")}
            >
              {t('landing.footer.usefulLinks.testimonials')}
            </a>
          </div>

          {/* Actions (Desktop) */}
          <div className="hidden md:flex md:items-center md:space-x-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <Button asChild variant="ghost" className="mr-2">
              <Link to={getPath('/auth')} className="font-medium">
                {t('navigation.login')}
              </Link>
            </Button>
            {user && (
              <Button 
                onClick={signOut} 
                className="mr-2 font-medium rounded-full bg-flame hover:bg-flame/90 text-white px-4 py-2 text-sm"
              >
                {t('navigation.logout')}
              </Button>
            )}
            {!user && (
              <Button asChild className="rounded-full">
                <Link to={getPath('/auth')} className="px-4 py-2 text-sm">
                  {t('navigation.signup')}
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              className="menu-toggle ml-2 p-2 rounded-md text-gray-500 hover:text-black hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
              onClick={toggleMenu}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
              <span className="sr-only">Toggle menu</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`mobile-menu absolute top-full left-0 right-0 bg-white dark:bg-gray-900 shadow-lg transition-all duration-300 ease-in-out ${
          isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0 invisible"
        } overflow-hidden md:hidden`}
      >
        <div className="px-4 py-3 space-y-3">
          <a
            href="#features"
            className="block py-2 px-3 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={(e) => handleNavItemClick(e, "features")}
          >
            {t('landing.footer.usefulLinks.features')}
          </a>
          <a
            href="#pricing"
            className="block py-2 px-3 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={(e) => handleNavItemClick(e, "pricing")}
          >
            {t('landing.footer.usefulLinks.pricing')}
          </a>
          <a
            href="#testimonials"
            className="block py-2 px-3 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={(e) => handleNavItemClick(e, "testimonials")}
          >
            {t('landing.footer.usefulLinks.testimonials')}
          </a>
          <div className="pt-2 pb-3 border-t border-gray-200 dark:border-gray-700">
            <Link
              to={getPath('/auth')}
              className="block py-2 px-3 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('navigation.login')}
            </Link>
            {user && (
              <Button
                className="block w-full text-center py-2 px-3 rounded-full mt-2 bg-flame text-white hover:bg-flame/90"
                onClick={() => {
                  signOut();
                  setIsMenuOpen(false);
                }}
              >
                {t('navigation.logout')}
              </Button>
            )}
            {!user && (
              <Link
                to={getPath('/auth')}
                className="block text-center py-2 px-3 mt-2 bg-flame text-white rounded-full hover:bg-flame/90"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('navigation.signup')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
